import { Account, Client, Databases, Permission, Query, Role } from "appwrite";
import { isListingPublished } from "./listingPricing.js";
import { Sentry } from "./sentry.js";

const client = new Client()
    .setEndpoint(import.meta.env.VITE_APPWRITE_ENDPOINT)
    .setProject(import.meta.env.VITE_APPWRITE_PROJECT_ID);

const account = new Account(client);
const databases = new Databases(client);

function databaseId() {
    const id = import.meta.env.VITE_APPWRITE_DB_ID?.trim();
    if (!id) throw new Error("Set VITE_APPWRITE_DB_ID in your environment.");
    return id;
}

function clientsCollectionId() {
    return import.meta.env.VITE_APPWRITE_COLLECTION_CLIENTS?.trim() || "clients";
}

function coachesCollectionId() {
    return "coaches";
}

function sessionsCollectionId() {
    return "sessions";
}

function clientSchedulesCollectionId() {
    return "client_schedules";
}

function getRefId(value) {
    if (value == null) return null;
    if (typeof value === "string") {
        const id = value.trim();
        return id.length > 0 ? id : null;
    }
    if (typeof value === "object" && value.$id) {
        const id = String(value.$id).trim();
        return id.length > 0 ? id : null;
    }
    return null;
}

function programsCollectionId() {
    return import.meta.env.VITE_APPWRITE_COLLECTION_PROGRAMS?.trim() || "programs";
}

function shareableListingsCollectionId() {
    return "shareable_listings";
}

function normalizeListing(doc) {
    return {
        id: doc.$id,
        programId: doc.programId,
        title: doc.title,
        status: doc.status,
        isPublished: isListingPublished(doc),
        tagline: doc.tagline ?? "",
        description: doc.description ?? "",
        category: doc.category ?? "",
        level: doc.level ?? "",
        durationWeeks: doc.durationWeeks ?? "",
        price: Number(doc.price) || 0,
        pricingMode: doc.pricingMode ?? "",
        coachName: doc.coachNameSnapshot ?? "",
        coachBio: doc.coachBioSnapshot ?? "",
        thumbnailFileId: doc.thumbnailFileId ?? null,
        coverImageFileId: doc.coverImageFileId ?? null,
        createdAt: doc.$createdAt,
    };
}

function isLinkableClientStatus(status) {
    const lower = String(status ?? "").trim().toLowerCase();
    return lower === "active" || lower === "pending";
}

export async function resolveClientRosterId(userId, email) {
    const uid = String(userId ?? "").trim();
    if (!uid) return null;

    let userProfile = null;
    try {
        userProfile = await databases.getDocument({
            databaseId: databaseId(),
            collectionId: clientsCollectionId(),
            documentId: uid,
        });
    } catch {
    }

    const normalizedEmail = String(email ?? "").trim().toLowerCase();
    if (!normalizedEmail) return userProfile?.$id ? String(userProfile.$id) : null;

    try {
        const { documents } = await databases.listDocuments({
            databaseId: databaseId(),
            collectionId: clientsCollectionId(),
            queries: [Query.equal("email", normalizedEmail), Query.limit(25)],
        });
        const linkable = documents.filter((doc) => isLinkableClientStatus(doc.status));
        const assigned = linkable.filter((doc) => getRefId(doc.coachId));
        const doc = (assigned[0] ?? userProfile ?? linkable[0] ?? documents[0]) ?? null;
        return doc?.$id ? String(doc.$id) : null;
    } catch {
        return userProfile?.$id ? String(userProfile.$id) : null;
    }
}

/** Fallback when Auth prefs lack accountKind (e.g. demo users created in Appwrite Console). */
export async function resolveAccountKindFromProfile(userId, email) {
    const uid = String(userId ?? "").trim();
    if (!uid) return null;
    const db = databaseId();
    const normalizedEmail = String(email ?? "").trim().toLowerCase();

    try {
        await databases.getDocument({
            databaseId: db,
            collectionId: clientsCollectionId(),
            documentId: uid,
        });
        return "client";
    } catch {
        // not found by document id
    }

    if (normalizedEmail) {
        try {
            const { documents } = await databases.listDocuments({
                databaseId: db,
                collectionId: clientsCollectionId(),
                queries: [Query.equal("email", normalizedEmail), Query.limit(1)],
            });
            if (documents.length > 0) return "client";
        } catch {
            // roster lookup not permitted or failed
        }
    }

    try {
        const { documents } = await databases.listDocuments({
            databaseId: db,
            collectionId: clientSchedulesCollectionId(),
            queries: [Query.equal("clientId", uid), Query.limit(1)],
        });
        if (documents.length > 0) return "client";
    } catch {
        // schedule lookup not permitted or failed
    }

    try {
        const { documents } = await databases.listDocuments({
            databaseId: db,
            collectionId: sessionsCollectionId(),
            queries: [Query.equal("clientId", uid), Query.limit(1)],
        });
        if (documents.length > 0) return "client";
    } catch {
        // session lookup not permitted or failed
    }

    try {
        await databases.getDocument({
            databaseId: db,
            collectionId: coachesCollectionId(),
            documentId: uid,
        });
        return "coach";
    } catch {
        return null;
    }
}

function splitDisplayName(fullName) {
    const trimmed = String(fullName ?? "").trim();
    if (!trimmed) return { firstName: "", lastName: "" };
    const spaceIdx = trimmed.indexOf(" ");
    if (spaceIdx <= 0) return { firstName: trimmed, lastName: "" };
    return {
        firstName: trimmed.slice(0, spaceIdx).trim(),
        lastName: trimmed.slice(spaceIdx + 1).trim(),
    };
}

export async function createUserProfileDocument(userId, name, email, accountKind) {
    const isClient = accountKind === "client";
    const collectionId = isClient ? clientsCollectionId() : coachesCollectionId();
    const { firstName, lastName } = splitDisplayName(name);

    try {
        await databases.createDocument({
            databaseId: databaseId(),
            collectionId,
            documentId: userId,
            data: {
                firstName: firstName || "",
                lastName: lastName || "",
                email: isClient ? String(email ?? "").trim().toLowerCase() : email,
                status: isClient ? "pending" : "active",
            },
            permissions: [
                Permission.read(Role.user(userId)),
                Permission.update(Role.user(userId)),
            ],
        });
    } catch (err) {
        // A repeated profile write is harmless; all other failures need visibility.
        if (err?.code === 409) return;
        Sentry.captureException(err, {
            tags: {
                operation: "create_user_profile",
                accountKind: isClient ? "client" : "coach",
            },
            extra: {
                userId,
                collectionId,
            },
        });
    }
}

async function loadCoachDisplayName(coachId) {
    const id = String(coachId ?? "").trim();
    if (!id) return "";

    try {
        const coach = await databases.getDocument({
            databaseId: databaseId(),
            collectionId: coachesCollectionId(),
            documentId: id,
        });
        const parts = [coach.firstName, coach.lastName].filter(Boolean);
        return parts.join(" ").trim();
    } catch {
        return "";
    }
}

async function loadClientProgramAccess(clientId) {
    const uid = String(clientId ?? "").trim();
    if (!uid) {
        return { currentProgramId: null, previousProgramIds: [], lastActiveByProgramId: new Map() };
    }

    const db = databaseId();
    const [scheduleRes, sessionsRes] = await Promise.all([
        databases.listDocuments({
            databaseId: db,
            collectionId: clientSchedulesCollectionId(),
            queries: [Query.equal("clientId", uid), Query.limit(1)],
        }),
        databases.listDocuments({
            databaseId: db,
            collectionId: sessionsCollectionId(),
            queries: [Query.equal("clientId", uid), Query.limit(100)],
        }),
    ]);

    const schedule = scheduleRes.documents[0];
    const currentProgramId = getRefId(schedule?.programId);

    const lastActiveByProgramId = new Map();
    for (const session of sessionsRes.documents) {
        const programId = getRefId(session.programId);
        if (!programId) continue;

        const activityAt = session.completedAt
            || session.scheduledAt
            || session.$updatedAt
            || session.$createdAt;
        const prev = lastActiveByProgramId.get(programId);
        if (!prev || new Date(activityAt) > new Date(prev)) {
            lastActiveByProgramId.set(programId, activityAt);
        }
    }

    const previousProgramIds = [...lastActiveByProgramId.keys()]
        .filter((programId) => programId !== currentProgramId)
        .sort((a, b) => new Date(lastActiveByProgramId.get(b)) - new Date(lastActiveByProgramId.get(a)));

    return {
        schedule,
        currentProgramId,
        previousProgramIds,
        lastActiveByProgramId,
    };
}

async function clientHasProgramAccess(clientId, programId) {
    const id = String(programId ?? "").trim();
    if (!id) return false;

    const { currentProgramId, previousProgramIds } = await loadClientProgramAccess(clientId);
    return currentProgramId === id || previousProgramIds.includes(id);
}

async function buildClientProgramEntry(programId, { assignedAt, lastActiveAt }) {
    const id = String(programId ?? "").trim();
    if (!id) return null;

    try {
        const program = await databases.getDocument({
            databaseId: databaseId(),
            collectionId: programsCollectionId(),
            documentId: id,
        });
        const coachName = await loadCoachDisplayName(program.coachId);

        return {
            id,
            title: String(program.title ?? "").trim() || "Untitled program",
            coachName,
            assignedAt: assignedAt ?? null,
            lastActiveAt: lastActiveAt ?? null,
        };
    } catch {
        return null;
    }
}

export async function getClientPrograms(userId, email) {
    let resolvedEmail = email;
    if (!resolvedEmail && userId) {
        try {
            const user = await account.get();
            if (String(user.$id) === String(userId)) {
                resolvedEmail = user.email;
            }
        } catch {
        }
    }

    const clientId = await resolveClientRosterId(userId, resolvedEmail);
    if (!clientId) return { current: [], previous: [] };

    const {
        schedule,
        currentProgramId,
        previousProgramIds,
        lastActiveByProgramId,
    } = await loadClientProgramAccess(clientId);

    const currentEntry = currentProgramId
        ? await buildClientProgramEntry(currentProgramId, {
            assignedAt: schedule?.$updatedAt ?? schedule?.$createdAt ?? null,
            lastActiveAt: lastActiveByProgramId.get(currentProgramId) ?? null,
        })
        : null;

    const previousEntries = (
        await Promise.all(
            previousProgramIds.map((programId) =>
                buildClientProgramEntry(programId, {
                    lastActiveAt: lastActiveByProgramId.get(programId) ?? null,
                }),
            ),
        )
    ).filter(Boolean);

    return {
        current: currentEntry ? [currentEntry] : [],
        previous: previousEntries,
    };
}

/** Merge purchased shareable listings into current programs and remove them from previous. */
export function mergeClientProgramsWithOwnedListings(base, ownedListings = []) {
    const ownedListingIds = new Set();
    const ownedProgramIds = new Set();

    const ownedEntries = (ownedListings ?? []).map((listing) => {
        const id = String(listing.id ?? "").trim();
        const programId = String(listing.programId ?? "").trim();
        if (id) ownedListingIds.add(id);
        if (programId) ownedProgramIds.add(programId);

        return {
            id,
            programId: programId || null,
            title: String(listing.title ?? "").trim() || "Untitled program",
            coachName: String(listing.coachName ?? "").trim(),
            dateLabel: "Purchased",
            dateValue: listing.purchasedAt ?? null,
            source: "listing",
        };
    }).filter((entry) => entry.id);

    const currentCoachIds = new Set((base.current ?? []).map((program) => program.id));

    const current = [
        ...(base.current ?? []).map((program) => ({
            ...program,
            dateLabel: "Assigned",
            dateValue: program.assignedAt ?? null,
        })),
        ...ownedEntries.filter(
            (entry) =>
                !currentCoachIds.has(entry.id)
                && !(entry.programId && currentCoachIds.has(entry.programId)),
        ),
    ];

    const previous = (base.previous ?? [])
        .filter(
            (program) =>
                !ownedListingIds.has(program.id)
                && !ownedProgramIds.has(program.id),
        )
        .map((program) => ({
            ...program,
            dateLabel: "Last active",
            dateValue: program.lastActiveAt ?? null,
        }));

    return { current, previous };
}


export async function getClientProgramView(id, userId, email) {
    const viewId = String(id ?? "").trim();
    if (!viewId) return null;

    let resolvedEmail = email;
    if (!resolvedEmail && userId) {
        try {
            const user = await account.get();
            if (String(user.$id) === String(userId)) {
                resolvedEmail = user.email;
            }
        } catch {
        }
    }

    const clientId = userId ? await resolveClientRosterId(userId, resolvedEmail) : null;

    const listing = await getProgramById(viewId);
    if (listing) {
        return { ...listing, viewType: "listing" };
    }

    if (!clientId || !(await clientHasProgramAccess(clientId, viewId))) {
        return null;
    }

    try {
        const program = await databases.getDocument({
            databaseId: databaseId(),
            collectionId: programsCollectionId(),
            documentId: viewId,
        });
        const coachName = await loadCoachDisplayName(program.coachId);

        return {
            id: program.$id,
            programId: program.$id,
            title: String(program.title ?? "").trim() || "Untitled program",
            description: String(program.description ?? "").trim(),
            tagline: "",
            category: "",
            level: "",
            durationWeeks: program.weeks != null ? String(program.weeks) : "",
            price: 0,
            pricingMode: "",
            isPublished: true,
            coachName,
            viewType: "assigned",
        };
    } catch {
        return null;
    }
}

export async function getProgramById(listingId) {
    const id = String(listingId ?? "").trim();
    if (!id) return null;

    try {
        const doc = await databases.getDocument({
            databaseId: databaseId(),
            collectionId: shareableListingsCollectionId(),
            documentId: id,
        });
        if (!isListingPublished(doc)) return null;
        return normalizeListing(doc);
    } catch {
        return null;
    }
}

export async function getCoachListingById(coachId, listingId) {
    const coach = String(coachId ?? "").trim();
    const id = String(listingId ?? "").trim();
    if (!coach || !id) return null;

    try {
        const doc = await databases.getDocument({
            databaseId: databaseId(),
            collectionId: shareableListingsCollectionId(),
            documentId: id,
        });
        const program = await databases.getDocument({
            databaseId: databaseId(),
            collectionId: programsCollectionId(),
            documentId: doc.programId,
        });
        if (String(program.coachId ?? "").trim() !== coach) return null;
        return normalizeListing(doc);
    } catch {
        return null;
    }
}

export async function getCoachProgramListings(coachId) {
    const coach = String(coachId ?? "").trim();
    if (!coach) throw new Error("Missing coach id.");
    const db = databaseId();

    // Listings have no coachId; ownership is derived via programId -> programs.coachId.
    const { documents: programs } = await databases.listDocuments({
        databaseId: db,
        collectionId: programsCollectionId(),
        queries: [Query.equal("coachId", coach), Query.limit(100)],
    });

    const programIds = programs.map((p) => p.$id);
    if (programIds.length === 0) return [];

    const { documents: listings } = await databases.listDocuments({
        databaseId: db,
        collectionId: shareableListingsCollectionId(),
        queries: [Query.equal("programId", programIds), Query.limit(100)],
    });

    return listings.map(normalizeListing);
}

export { account, client, databases };

